<template>
  <div v-if="visible" class="modal-overlay" @click="handleOverlayClick">
    <div class="modal-container" @click.stop>
      <div class="modal-header">
        <h3>新增设备</h3>
        <button @click="handleClose" class="close-btn">&times;</button>
      </div>
      
      <div class="modal-body">
        <form @submit.prevent="handleSubmit" class="device-form">
          <div class="form-group">
            <label for="deviceCode" class="required">设备编码</label>
            <input
              id="deviceCode"
              v-model="formData.deviceCode"
              type="text"
              placeholder="请输入设备编码（同门店不可重复）"
              class="form-input"
              :class="{ 'error': errors.deviceCode }"
              required
            />
            <span v-if="errors.deviceCode" class="error-message">{{ errors.deviceCode }}</span>
          </div>

          <div class="form-group">
            <label for="deviceName" class="required">设备名称</label>
            <input
              id="deviceName"
              v-model="formData.deviceName"
              type="text"
              placeholder="请输入设备名称"
              class="form-input"
              :class="{ 'error': errors.deviceName }"
              required
            />
            <span v-if="errors.deviceName" class="error-message">{{ errors.deviceName }}</span>
          </div>
          
          <div class="form-group">
            <label for="storeId" class="required">所属门店</label>
            <select
              id="storeId"
              v-model="formData.storeId"
              class="form-select"
              :class="{ 'error': errors.storeId }"
              required
            >
              <option value="">请选择门店</option>
              <option v-for="store in storeList" :key="store.id" :value="store.id">
                {{ store.storeName }}
              </option>
            </select>
            <span v-if="errors.storeId" class="error-message">{{ errors.storeId }}</span>
          </div>

          <div class="form-group">
            <label for="deviceType" class="required">设备类型</label>
            <select
              id="deviceType"
              v-model="formData.deviceType"
              class="form-select"
            >
              <option value="1">赛车模拟器/计时设备</option>
              <option value="2">智能插座</option>
              <option value="3">直接扣费项目设备</option>
            </select>
          </div>

          <div class="form-group" v-if="formData.deviceType !== '3'">
            <label for="countdownDelayMinutes">延迟开始计时（分钟）</label>
            <input
              id="countdownDelayMinutes"
              v-model.number="formData.countdownDelayMinutes"
              type="number"
              min="0"
              max="99"
              placeholder="为空时跟随门店配置"
              class="form-input"
            />
          </div>
          
          <div class="form-group">
            <label for="deviceIdentifier">设备标识（客户ID）</label>
            <input
              id="deviceIdentifier"
              v-model="formData.deviceIdentifier"
              type="text"
              placeholder="请输入设备标识"
              class="form-input"
              :class="{ 'error': errors.deviceIdentifier }"
            />
            <span v-if="errors.deviceIdentifier" class="error-message">{{ errors.deviceIdentifier }}</span>
          </div>

          <div v-if="formData.deviceType === '2'" class="topic-help">
            <p>注意：平台页面中的“订阅主题 / 发布主题”是按设备视角命名的，外部客户端接入时请勿按字面理解填写。</p>
            <p>设备详情里显示的“订阅主题”表示设备会订阅该主题，因此客户端应向该主题发布控制指令；设备详情里显示的“发布主题”表示设备会向该主题上报数据，因此客户端应订阅该主题接收设备消息。</p>
            <p>请按下面规则填写：</p>
            <p>1. 设备发布主题：对应 GeekOpen 页面“发布主题”。常见形态是 <code>/.../subscribe</code>。客户端应订阅这里，接收设备回包、状态和功率数据。</p>
            <p>2. 设备订阅主题：对应 GeekOpen 页面“订阅主题”。常见形态是 <code>/.../publish</code>。客户端应向这里发布控制指令。</p>
            <p>例如平台页面若显示：</p>
            <p><code>订阅主题：/lSavBf/.../c82b96f737ab/publish</code></p>
            <p><code>发布主题：/lSavBf/.../c82b96f737ab/subscribe</code></p>
            <p>那么本表单中应填写为：</p>
            <p><code>设备发布主题(topicDevicePublish) = /lSavBf/.../c82b96f737ab/subscribe</code></p>
            <p><code>设备订阅主题(topicDeviceSubscribe) = /lSavBf/.../c82b96f737ab/publish</code></p>
            <p>从客户端视角看，这会像“反填”：客户端订阅页面的“发布主题”，客户端发布到页面的“订阅主题”；如果把 <code>/publish</code> 和 <code>/subscribe</code> 按字面方向填反，就会出现设备在线但指令无响应。</p>
          </div>
          
          <div class="form-group">
            <label for="topicDevicePublish">设备发布主题（GeekOpen“发布主题”，通常以 /subscribe 结尾，客户端订阅这里）</label>
            <input
              id="topicDevicePublish"
              v-model="formData.topicDevicePublish"
              type="text"
              placeholder="填写 GeekOpen 页面“发布主题”，如 /xxx/.../subscribe"
              class="form-input"
              :class="{ 'error': errors.topicDevicePublish }"
            />
            <span v-if="errors.topicDevicePublish" class="error-message">{{ errors.topicDevicePublish }}</span>
          </div>

          <div class="form-group">
            <label for="topicDeviceSubscribe">设备订阅主题（GeekOpen“订阅主题”，通常以 /publish 结尾，客户端向这里发布）</label>
            <input
              id="topicDeviceSubscribe"
              v-model="formData.topicDeviceSubscribe"
              type="text"
              placeholder="填写 GeekOpen 页面“订阅主题”，如 /xxx/.../publish"
              class="form-input"
              :class="{ 'error': errors.topicDeviceSubscribe }"
            />
            <span v-if="errors.topicDeviceSubscribe" class="error-message">{{ errors.topicDeviceSubscribe }}</span>
          </div>

          <template v-if="formData.deviceType === '2'">
            <div class="form-group">
              <label for="mqttHost">MQTT地址</label>
              <input
                id="mqttHost"
                v-model="formData.mqttHost"
                type="text"
                placeholder="如 mqtt.smart-bird.cn"
                class="form-input"
                :class="{ 'error': errors.mqttHost }"
              />
              <span v-if="errors.mqttHost" class="error-message">{{ errors.mqttHost }}</span>
            </div>

            <div class="form-group">
              <label for="mqttPort">MQTT端口</label>
              <input
                id="mqttPort"
                v-model.number="formData.mqttPort"
                type="number"
                min="1"
                placeholder="如 1883"
                class="form-input"
                :class="{ 'error': errors.mqttPort }"
              />
              <span v-if="errors.mqttPort" class="error-message">{{ errors.mqttPort }}</span>
            </div>

            <div class="form-group">
              <label for="mqttClientId">MQTT Client ID</label>
              <input
                id="mqttClientId"
                v-model="formData.mqttClientId"
                type="text"
                placeholder="请输入服务端 MQTT Client ID"
                class="form-input"
                :class="{ 'error': errors.mqttClientId }"
              />
              <span v-if="errors.mqttClientId" class="error-message">{{ errors.mqttClientId }}</span>
            </div>

            <div class="form-group">
              <label for="mqttUsername">MQTT用户名</label>
              <input
                id="mqttUsername"
                v-model="formData.mqttUsername"
                type="text"
                placeholder="请输入 MQTT 用户名"
                class="form-input"
                :class="{ 'error': errors.mqttUsername }"
              />
              <span v-if="errors.mqttUsername" class="error-message">{{ errors.mqttUsername }}</span>
            </div>

            <div class="form-group">
              <label for="mqttPassword">MQTT密码</label>
              <input
                id="mqttPassword"
                v-model="formData.mqttPassword"
                type="text"
                placeholder="请输入 MQTT 密码"
                class="form-input"
                :class="{ 'error': errors.mqttPassword }"
              />
              <span v-if="errors.mqttPassword" class="error-message">{{ errors.mqttPassword }}</span>
            </div>

            <div class="form-group">
              <label for="smartPlugCycleEnabled">是否启用空闲循环断电</label>
              <select
                id="smartPlugCycleEnabled"
                v-model="formData.smartPlugCycleEnabled"
                class="form-select"
              >
                <option :value="false">关闭</option>
                <option :value="true">开启</option>
              </select>
              <div class="form-help">关闭后不需要填写“每隔多少分钟断电一次”，系统不会自动循环断电。</div>
            </div>

            <div class="form-group" v-if="formData.smartPlugCycleEnabled">
              <label for="smartPlugCycleIntervalMinutes" class="required">每隔多少分钟断电一次</label>
              <input
                id="smartPlugCycleIntervalMinutes"
                v-model.number="formData.smartPlugCycleIntervalMinutes"
                type="number"
                min="1"
                placeholder="请输入循环间隔（分钟）"
                class="form-input"
                :class="{ 'error': errors.smartPlugCycleIntervalMinutes }"
              />
              <span v-if="errors.smartPlugCycleIntervalMinutes" class="error-message">{{ errors.smartPlugCycleIntervalMinutes }}</span>
            </div>

            <div class="form-group">
              <label for="smartPlugPowerOffSeconds" class="required">断电后多少秒恢复通电</label>
              <input
                id="smartPlugPowerOffSeconds"
                v-model.number="formData.smartPlugPowerOffSeconds"
                type="number"
                min="1"
                placeholder="请输入恢复秒数"
                class="form-input"
                :class="{ 'error': errors.smartPlugPowerOffSeconds }"
              />
              <span v-if="errors.smartPlugPowerOffSeconds" class="error-message">{{ errors.smartPlugPowerOffSeconds }}</span>
            </div>

          </template>
          
          <div class="form-group" v-if="formData.deviceType !== '2'">
            <label for="uuid">系统UUID</label>
            <select
              id="uuid"
              v-model="formData.uuid"
              class="form-select"
              :class="{ 'error': errors.uuid }"
            >
              <option value="">请选择系统UUID</option>
              <option v-for="item in systemUuidList" :key="item.id" :value="item.uuid">
                {{ item.uuid }}
              </option>
            </select>
            <span v-if="errors.uuid" class="error-message">{{ errors.uuid }}</span>
          </div>
          
          <div class="form-group">
            <label for="description">设备描述</label>
            <textarea
              id="description"
              v-model="formData.description"
              placeholder="请输入设备描述（可选）"
              class="form-textarea"
              rows="3"
            ></textarea>
          </div>
        </form>
      </div>
      
      <div class="modal-footer">
        <button @click="handleClose" class="cancel-btn">取消</button>
        <button @click="handleSubmit" class="submit-btn" :disabled="loading">
          {{ loading ? '保存中...' : '保存' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import request from '../utils/request'

export default {
  name: 'AddDeviceModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    storeList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'success'],
  data() {
    return {
      loading: false,
      systemUuidList: [],
      formData: {
        deviceCode: '',
        deviceName: '',
        deviceType: '1',
        storeId: '',
        description: '',
        deviceIdentifier: '',
        topicDevicePublish: '',
        topicDeviceSubscribe: '',
        mqttHost: '',
        mqttPort: 1883,
        mqttClientId: '',
        mqttUsername: '',
        mqttPassword: '',
        uuid: '',
        countdownDelayMinutes: 0,
        smartPlugCycleEnabled: false,
        smartPlugCycleIntervalMinutes: 30,
        smartPlugPowerOffSeconds: 60
      },
      errors: {}
    }
  },
  watch: {
    visible(newVal) {
      if (newVal) {
        this.resetForm()
        this.loadSystemUuids()
      }
    }
  },
  methods: {
    resetForm() {
      this.formData = {
        deviceCode: '',
        deviceName: '',
        deviceType: '1',
        storeId: '',
        description: '',
        deviceIdentifier: '',
        topicDevicePublish: '',
        topicDeviceSubscribe: '',
        mqttHost: '',
        mqttPort: 1883,
        mqttClientId: '',
        mqttUsername: '',
        mqttPassword: '',
        uuid: '',
        countdownDelayMinutes: 0,
        smartPlugCycleEnabled: false,
        smartPlugCycleIntervalMinutes: 30,
        smartPlugPowerOffSeconds: 60
      }
      this.errors = {}
      this.loading = false
    },
    
    async loadSystemUuids() {
      try {
        const response = await request.get('/api/devices/getAllSystemUuids')
        
        if (response.data.success && response.data.code === 200) {
          this.systemUuidList = response.data.data || []
        } else {
          console.error('获取系统UUID列表失败:', response.data.message)
          this.systemUuidList = []
        }
      } catch (error) {
        console.error('获取系统UUID列表失败:', error)
        this.systemUuidList = []
      }
    },
    
    validateForm() {
      this.errors = {}
      let isValid = true

      if (!this.formData.deviceCode.trim()) {
        this.errors.deviceCode = '请输入设备编码'
        isValid = false
      }
      
      if (!this.formData.deviceName.trim()) {
        this.errors.deviceName = '请输入设备名称'
        isValid = false
      }
      
      if (!this.formData.storeId) {
        this.errors.storeId = '请选择所属门店'
        isValid = false
      }

      if (this.formData.deviceType === '2') {
        if (this.formData.smartPlugCycleEnabled
          && (!this.formData.smartPlugCycleIntervalMinutes || this.formData.smartPlugCycleIntervalMinutes <= 0)) {
          this.errors.smartPlugCycleIntervalMinutes = '请输入大于 0 的循环间隔'
          isValid = false
        }
        if (!this.formData.smartPlugPowerOffSeconds || this.formData.smartPlugPowerOffSeconds <= 0) {
          this.errors.smartPlugPowerOffSeconds = '请输入大于 0 的恢复秒数'
          isValid = false
        }
      }

      if (this.formData.deviceType === '2') {
        const mqttFields = [
          ['mqttHost', '请输入 MQTT 地址'],
          ['mqttPort', '请输入正确的 MQTT 端口'],
          ['mqttClientId', '请输入 MQTT Client ID'],
          ['mqttUsername', '请输入 MQTT 用户名'],
          ['mqttPassword', '请输入 MQTT 密码'],
          ['deviceIdentifier', '请输入设备标识'],
          ['topicDevicePublish', '请输入设备发布主题'],
          ['topicDeviceSubscribe', '请输入设备订阅主题']
        ]
        mqttFields.forEach(([field, message]) => {
          const value = this.formData[field]
          if (field === 'mqttPort') {
            if (!value || value <= 0) {
              this.errors[field] = message
              isValid = false
            }
          } else if (!value || !String(value).trim()) {
            this.errors[field] = message
            isValid = false
          }
        })
      }
      
      return isValid
    },
    
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }
      
      this.loading = true
      
      try {
        const requestData = {
          deviceCode: this.formData.deviceCode.trim(),
          deviceName: this.formData.deviceName.trim(),
          deviceType: this.formData.deviceType,
          storeId: Number(this.formData.storeId),
          description: this.formData.description.trim(),
          deviceIdentifier: this.formData.deviceIdentifier.trim() || null,
          topicDevicePublish: this.formData.topicDevicePublish.trim() || null,
          topicDeviceSubscribe: this.formData.topicDeviceSubscribe.trim() || null,
          mqttHost: this.formData.deviceType === '2' ? this.formData.mqttHost.trim() || null : null,
          mqttPort: this.formData.deviceType === '2' ? Number(this.formData.mqttPort) || null : null,
          mqttClientId: this.formData.deviceType === '2' ? this.formData.mqttClientId.trim() || null : null,
          mqttUsername: this.formData.deviceType === '2' ? this.formData.mqttUsername.trim() || null : null,
          mqttPassword: this.formData.deviceType === '2' ? this.formData.mqttPassword.trim() || null : null,
          uuid: this.formData.deviceType === '2' ? null : (this.formData.uuid || null),
          countdownDelayMinutes: this.formData.deviceType === '3' ? 0 : (Number(this.formData.countdownDelayMinutes) || 0),
          smartPlugCycleEnabled: this.formData.deviceType === '2' ? this.formData.smartPlugCycleEnabled : false,
          smartPlugCycleIntervalMinutes: this.formData.deviceType === '2' && this.formData.smartPlugCycleEnabled
            ? Number(this.formData.smartPlugCycleIntervalMinutes)
            : null,
          smartPlugPowerOffSeconds: this.formData.deviceType === '2'
            ? Number(this.formData.smartPlugPowerOffSeconds)
            : null
        }
        
        const response = await request.post('/api/devices/createDevice', requestData)
        
        if (response.data.success && response.data.code === 200) {
          this.$emit('success', response.data.data)
          this.handleClose()
        } else {
          throw new Error(response.data.message || '新增设备失败')
        }
        
      } catch (error) {
        console.error('新增设备失败:', error)
        let errorMessage = '新增设备失败'
        
        if (error.response) {
          errorMessage = error.response.data.message || `服务器错误: ${error.response.status}`
        } else if (error.request) {
          errorMessage = '网络连接失败，请检查网络或服务器状态'
        } else {
          errorMessage = error.message || '新增设备失败，请重试'
        }
        
        alert(errorMessage)
      } finally {
        this.loading = false
      }
    },
    
    handleClose() {
      this.$emit('close')
    },
    
    handleOverlayClick() {
      if (!this.loading) {
        this.handleClose()
      }
    }
  }
}
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.modal-container {
  background: white;
  border-radius: 8px;
  width: 90%;
  max-width: 500px;
  max-height: 90vh;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px 24px;
  border-bottom: 1px solid #f0f0f0;
}

.modal-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  font-size: 24px;
  color: #999;
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #666;
}

.modal-body {
  padding: 24px;
  max-height: 60vh;
  overflow-y: auto;
}

.device-form {
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.form-group label {
  font-weight: 500;
  color: #333;
  font-size: 14px;
}

.form-group label.required::after {
  content: ' *';
  color: #ff4d4f;
}

.form-input, .form-textarea, .form-select {
  padding: 10px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  font-size: 14px;
  transition: border-color 0.3s ease;
  font-family: inherit;
}

.form-input:focus, .form-textarea:focus, .form-select:focus {
  outline: none;
  border-color: #1890ff;
  box-shadow: 0 0 0 2px rgba(24, 144, 255, 0.2);
}

.form-input.error, .form-textarea.error, .form-select.error {
  border-color: #ff4d4f;
}

.form-select {
  background-color: white;
  cursor: pointer;
}

.form-select option {
  padding: 8px;
}

.form-textarea {
  resize: vertical;
  min-height: 80px;
}

.error-message {
  color: #ff4d4f;
  font-size: 12px;
  margin-top: 4px;
}

.form-help {
  color: #8c8c8c;
  font-size: 12px;
  margin-top: 6px;
}

.topic-help {
  padding: 12px 14px;
  border-radius: 6px;
  background: #f6fbff;
  border: 1px solid #d6ecff;
  color: #35526b;
  font-size: 13px;
  line-height: 1.6;
}

.topic-help p {
  margin: 0;
}

.topic-help p + p {
  margin-top: 6px;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  padding: 20px 24px;
  border-top: 1px solid #f0f0f0;
  background: #fafafa;
}

.cancel-btn, .submit-btn {
  padding: 8px 20px;
  border: 1px solid #d9d9d9;
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  transition: all 0.3s ease;
}

.cancel-btn {
  background: white;
  color: #666;
}

.cancel-btn:hover {
  background: #f5f5f5;
  border-color: #40a9ff;
}

.submit-btn {
  background: #1890ff;
  color: white;
  border-color: #1890ff;
}

.submit-btn:hover:not(:disabled) {
  background: #40a9ff;
  border-color: #40a9ff;
}

.submit-btn:disabled {
  background: #d9d9d9;
  border-color: #d9d9d9;
  cursor: not-allowed;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .modal-container {
    width: 95%;
    margin: 20px;
  }
  
  .modal-header, .modal-body, .modal-footer {
    padding: 16px;
  }
  
  .modal-footer {
    flex-direction: column;
  }
  
  .cancel-btn, .submit-btn {
    width: 100%;
  }
}
</style>
